/**
 * Report an Issue — shared contract + row types (PRO-0550).
 *
 * Single source of truth for the `/issues` request/response shapes and the DB
 * row interfaces, consumed by BOTH tiers: the API (`apps/api`, building the
 * response), the SPA (`apps/web`, capturing + rendering triage), and the worker
 * (`apps/worker`, notification). The SPA imports these as `import type`, so
 * nothing in the shared db/domain lib is pulled into the browser bundle.
 *
 * PII posture: the free-text fields (`tried`/`happened`/`extraDetails`) are the
 * reporter's own submission and MAY carry incidental PII — they are the point of
 * the report, but must never enter logs/audit/alerts (SEC-8). `context` is
 * non-PII by construction (SF-SEC-001 — pathname-only, server-normalised).
 */

export type IssueType =
  | 'bug'
  | 'data_issue'
  | 'access_problem'
  | 'performance'
  | 'other'
  | 'improvement';

export type IssueQuickTag = 'did_not_load' | 'frozen' | 'error_message' | 'wrong_data';

export type IssueReportStatus =
  | 'new'
  | 'acknowledged'
  | 'closed'
  | 'archived'
  | 'accepted_change'
  | 'accepted_defect';

export type IssueTriageBucket = 'open' | 'archived' | 'accepted' | 'legacy';

/** Completion of an accepted change or defect. Not a triage status. */
export type IssueCompletionStatus = 'not_completed' | 'completed';

/** `GET /issues?bucket=accepted&completion=` */
export type IssueCompletionFilter = 'all' | IssueCompletionStatus;

/** The valid quick-tag set (also enforced by the DB CHECK). */
export const ISSUE_TYPES: readonly IssueType[] = [
  'bug',
  'data_issue',
  'access_problem',
  'performance',
  'other',
  'improvement',
];

export const ISSUE_QUICK_TAGS: readonly IssueQuickTag[] = [
  'did_not_load',
  'frozen',
  'error_message',
  'wrong_data',
];

export const ISSUE_REPORT_STATUSES: readonly IssueReportStatus[] = [
  'new',
  'acknowledged',
  'closed',
  'archived',
  'accepted_change',
  'accepted_defect',
];

export const ISSUE_TRIAGE_BUCKETS: readonly IssueTriageBucket[] = [
  'open',
  'archived',
  'accepted',
  'legacy',
];

export const OPEN_STATUSES: readonly IssueReportStatus[] = ['new', 'acknowledged'];
export const ACCEPTED_STATUSES: readonly IssueReportStatus[] = ['accepted_change', 'accepted_defect'];

export const STATUSES_BY_BUCKET: Record<IssueTriageBucket, readonly IssueReportStatus[]> = {
  open: OPEN_STATUSES,
  archived: ['archived'],
  accepted: ACCEPTED_STATUSES,
  legacy: ['closed'],
};

export const PATCHABLE_STATUSES: readonly IssueReportStatus[] = [
  'acknowledged',
  'archived',
  'accepted_change',
  'accepted_defect',
];

/** Length caps applied at the API boundary + backstopped by the DB CHECKs. */
export const ISSUE_TEXT_MAX = 4000;
export const ISSUE_EXTRA_DETAILS_MAX = 8000;
export const ISSUE_QUICK_TAGS_MAX = 4;
export const ISSUE_DISPOSITION_REASON_MAX = 4000;
export const ISSUE_ARTIFACT_MAX = 16000;
export const ISSUE_STORY_LINE_MAX = 4000;

/** Per-field length caps for the auto-captured technical context (SF-SEC-001). */
export const ISSUE_CONTEXT_CAPS = {
  routePath: 512,
  appVersion: 64,
  viewport: 32,
  userAgent: 512,
} as const;

/**
 * Auto-captured, non-PII technical context (AC-5). `routePath` is
 * `window.location.pathname` ONLY (never search/hash) — the server strips any
 * `?`/`#` remainder and length-caps each field before persistence (SF-SEC-001).
 */
export interface IssueReportContext {
  readonly routePath: string;
  readonly appVersion: string;
  readonly viewport: string; // e.g. "1440x900"
  readonly userAgent: string;
}

/** `POST /issues` request body (MVP, JSON). */
export interface CreateIssueReportRequest {
  readonly issueType: IssueType;
  readonly tried: string; // 1..4000
  readonly happened: string; // 1..4000
  readonly quickTags?: readonly IssueQuickTag[]; // 0..4, unique
  readonly extraDetails?: string; // <= 8000
  readonly context: IssueReportContext;
  readonly idempotencyKey: string; // client-generated UUID (API-4)
}

/** `POST /issues` response body. */
export interface CreateIssueReportResponse {
  readonly reference: string; // "ISS-000123"
  readonly status: 'new';
}

/** Triage list item — summary only (no free-text) to minimise exposure. */
export interface IssueReportSummary {
  readonly issueReportId: string;
  readonly reference: string;
  readonly issueType: IssueType;
  readonly status: IssueReportStatus;
  readonly quickTags: readonly IssueQuickTag[];
  readonly attachmentCount: number;
  readonly reporterDisplayName: string | null;
  readonly createdAt: string; // ISO-8601
  readonly completionStatus: IssueCompletionStatus;
}

export interface IssueReportListResponse {
  readonly items: readonly IssueReportSummary[];
  readonly nextCursor: string | null;
}

/** Triage full-detail read (`GET /issues/:id`) — audited privileged read (SF-001). */
export interface IssueReportDetail extends IssueReportSummary {
  readonly tried: string;
  readonly happened: string;
  readonly extraDetails: string | null;
  readonly context: IssueReportContext;
  readonly reporterOid: string;
  readonly dispositionReason: string | null;
  readonly actionArtifact: string | null;
  readonly actionArtifactPolished: string | null;
  readonly polishedAt: string | null;
  readonly polishedBy: string | null;
  readonly actionedAt: string | null;
  readonly actionedBy: string | null;
}

/** `PUT /issues/:id/polish-story` body — L6-edited triplet; server rebuilds the pack. */
export interface PolishFeedbackStoryRequest {
  readonly asA: string;
  readonly iWant: string;
  readonly soThat: string;
}

/** `POST /issues/:id/polish-story` suggestion (not persisted until PUT). */
export interface PolishFeedbackStoryResponse {
  readonly asA: string;
  readonly iWant: string;
  readonly soThat: string;
  readonly suggestedPack: string;
  readonly promptVersion: string;
}

/** `PATCH /issues/:id` request body. `closed` is legacy — not accepted on new writes. */
export interface UpdateIssueReportRequest {
  readonly status: 'acknowledged' | 'archived' | 'accepted_change' | 'accepted_defect';
  readonly dispositionReason?: string;
}

/** Completion-only PATCH. Must not be combined with `status`. */
export interface SetIssueCompletionRequest {
  readonly completionStatus: IssueCompletionStatus;
}

/** Service result: detail (+ previousStatus for logs), 404-null, or FSM conflict (route → 409). */
export type UpdateIssueReportResult =
  | (IssueReportDetail & { readonly previousStatus: IssueReportStatus })
  | { readonly conflict: true; readonly fromStatus: IssueReportStatus };

// ---------------------------------------------------------------------------
// Service-layer input + row types (DB-4: explicit typing, no `any`)
// ---------------------------------------------------------------------------

/** `IssueReportService.create` input — reporter identity is server-derived. */
export interface CreateIssueReportInput {
  readonly issueType: IssueType;
  readonly tried: string;
  readonly happened: string;
  readonly quickTags: readonly IssueQuickTag[];
  readonly extraDetails: string | null;
  readonly context: IssueReportContext;
  readonly reporterOid: string;
  readonly reporterDisplayName: string | null;
  readonly idempotencyKey: string;
}

export interface TriageListParams {
  readonly limit: number;
  readonly cursor?: string;
  readonly bucket: IssueTriageBucket;
  /** Accepted bucket only. Omitted means `all`. */
  readonly completion?: IssueCompletionFilter;
}

/** A row awaiting notification (the TD-2 sweep work item). */
export interface PendingIssueNotification {
  readonly issueReportId: string;
  readonly reference: string;
  readonly issueType: IssueType;
  readonly tried: string;
  readonly happened: string;
  readonly quickTags: readonly IssueQuickTag[];
  readonly extraDetails: string | null;
  readonly context: IssueReportContext;
  readonly reporterDisplayName: string | null;
  readonly attachmentCount: number;
  readonly createdAt: string; // ISO-8601
}

/** Raw `issue_report` row (full detail). */
export interface IssueReportRow {
  readonly issue_report_id: string;
  readonly reference: string;
  readonly issue_type: IssueType;
  readonly tried: string;
  readonly happened: string;
  readonly quick_tags: string[];
  readonly extra_details: string | null;
  readonly reporter_oid: string;
  readonly reporter_display_name: string | null;
  readonly context: IssueReportContext;
  readonly attachment_count: number;
  readonly status: IssueReportStatus;
  readonly created_at: Date;
  readonly disposition_reason: string | null;
  readonly action_artifact: string | null;
  readonly action_artifact_polished: string | null;
  readonly polished_at: Date | null;
  readonly polished_by: string | null;
  readonly actioned_at: Date | null;
  readonly actioned_by: string | null;
  readonly completion_status: IssueCompletionStatus;
  readonly completed_at: Date | null;
  readonly completed_by: string | null;
}

/** Raw `issue_report` row projected to the triage summary columns. */
export interface IssueReportSummaryRow {
  readonly issue_report_id: string;
  readonly reference: string;
  readonly issue_type: IssueType;
  readonly status: IssueReportStatus;
  readonly quick_tags: string[];
  readonly attachment_count: number;
  readonly reporter_display_name: string | null;
  readonly created_at: Date;
  readonly completion_status: IssueCompletionStatus;
}

/** Raw pending-notification row (the sweep projection). */
export interface PendingIssueNotificationRow {
  readonly issue_report_id: string;
  readonly reference: string;
  readonly issue_type: IssueType;
  readonly tried: string;
  readonly happened: string;
  readonly quick_tags: string[];
  readonly extra_details: string | null;
  readonly context: IssueReportContext;
  readonly reporter_display_name: string | null;
  readonly attachment_count: number;
  readonly created_at: Date;
}
